import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { BreadcrumbService } from '../../core/navigation';
import { BrandMarkComponent, IconButtonComponent, IconComponent, PopoverComponent } from '../../ui';
import { ViewportService } from '../shell/viewport.service';

/**
 * Testata (FR-025, FR-026): percorso, ricerca richiamabile con ⌘K / Ctrl+K e notifiche. In
 * questa feature ricerca e notifiche aprono un pannello "in arrivo". Sotto i 1024 px compare il
 * pulsante del menu; sotto i 768 px la testata scende a 56 px con il marchio compatto.
 */
@Component({
  selector: 'bs-topbar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, BrandMarkComponent, IconButtonComponent, IconComponent, PopoverComponent],
  host: {
    class:
      'sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b border-stone-200 bg-white px-3 md:h-16 md:px-7 lg:border-b-0 dark:border-stone-800 dark:bg-stone-950',
    '(document:keydown)': 'onKeydown($event)',
  },
  template: `
    @if (!viewport.isDesktop()) {
      <bs-icon-button
        icon="menu"
        label="Apri il menu di navigazione"
        controls="mobile-drawer"
        hasPopup="dialog"
        [expanded]="menuExpanded()"
        testId="menu-button"
        (click)="menuRequested.emit()"
      />
    }
    @if (viewport.isMobile()) {
      <a routerLink="/" aria-label="B.Site, vai alla Dashboard" class="rounded-lg">
        <bs-brand-mark variant="compatta" [size]="16" />
      </a>
      <span class="flex-1"></span>
    } @else {
      <nav aria-label="Percorso" class="min-w-0 flex-1" data-testid="breadcrumb">
        <ol class="flex min-w-0 items-center gap-2 text-sm">
          @for (crumb of crumbs(); track $index; let last = $last) {
            <li
              class="flex min-w-0 items-center gap-2"
              [class.shrink-0]="last && crumbs().length < 3"
            >
              @if (crumb.link !== null) {
                <a
                  [routerLink]="crumb.link"
                  [queryParams]="crumb.queryParams"
                  class="truncate text-stone-600 hover:text-ink dark:text-stone-300 dark:hover:text-stone-50"
                  [attr.title]="crumb.label"
                  [attr.data-testid]="'breadcrumb-' + crumb.kind"
                  >{{ crumb.label }}</a
                >
              } @else {
                <span
                  aria-current="page"
                  class="truncate font-semibold text-ink dark:text-stone-50"
                  [attr.title]="crumb.label"
                  data-testid="breadcrumb-current"
                  >{{ crumb.label }}</span
                >
              }
              @if (!last) {
                <span class="text-stone-500 dark:text-stone-400" aria-hidden="true">
                  <bs-icon name="chevron-right" [size]="14" />
                </span>
              }
            </li>
          }
        </ol>
      </nav>
      <bs-popover
        #search
        appearance="search"
        icon="search"
        [label]="'Cerca in B.Site (' + shortcut() + ')'"
        heading="Ricerca"
        placeholder="Cerca in B.Site"
        [shortcut]="shortcut()"
        testId="search"
      >
        <p>La ricerca è in arrivo: da qui potrai trovare cantieri, getti e documenti.</p>
      </bs-popover>
    }
    <bs-popover icon="bell" label="Notifiche" heading="Notifiche" testId="notifications">
      <p class="font-semibold text-ink dark:text-stone-50">Nessuna notifica</p>
      <p class="mt-1">
        Le notifiche sono in arrivo: qui troverai scadenze e novità dei tuoi cantieri.
      </p>
    </bs-popover>
  `,
})
export class TopbarComponent {
  /** Il pannello di navigazione è aperto (per `aria-expanded`). */
  readonly menuExpanded = input(false);
  readonly menuRequested = output();

  protected readonly viewport = inject(ViewportService);
  protected readonly crumbs = inject(BreadcrumbService).crumbs;
  private readonly document = inject(DOCUMENT);
  private readonly search = viewChild<PopoverComponent>('search');

  protected readonly shortcut = computed(() => {
    const platform = this.document.defaultView?.navigator.platform ?? '';
    return /mac|iphone|ipad/i.test(platform) ? '⌘K' : 'Ctrl K';
  });

  protected onKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      const search = this.search();
      if (search) {
        event.preventDefault();
        search.open();
      }
    }
  }
}
